<template>
  <slot></slot>
  <modal :id="modalId" :title="title">

    <template #header>
      <i class="fa fa-share-square-o modal-icon"></i>
      <h4 class="modal-title">Send for Packaging</h4>
    </template>

    <form :id="formId" :action="action" method="POST">

      <h3 v-if="!claimTeam || !claimAdjuster" class="text-center">
        <span class="text-danger">This claim doesn't have an assigned team and/or adjuster.</span><br>
        Please assign a team, an adjuster and an examiner before sending this claim for packaging.
      </h3>
      <template v-else>
        <div v-if="!examinerAssigned" class="text-left">
          <h3 class="text-center text-danger">
            This claim requires a valid examiner before it can be sent for packaging.
          </h3>
          <div v-if="assignClaimsPermission" class="form-group mt-3" :class="{'has-error': !this.data.examiner}">
            <div class="col-sm-12">
              <chosen name="examiner_id"
                      :options="examiners"
                      placeholder="Select an examiner..."
                      class="form-control form-control-sm" v-model="data.examiner"></chosen>
            </div>
          </div>
        </div>
        <hr/>
        <h3 v-if="invoicesCount > 0" class="text-center">
          Are you sure you want to approve the invoice and send this claim for packaging?
        </h3>
        <template v-else>
          <h3 class="text-center">
            <span class="text-danger">This claim doesn't have an invoice.</span>
            Are you sure you want to send it for packaging?
          </h3>
          <p class="text-center">
            Once in Packaging or Closed, the claim will have to be reopened in order to add an invoice.
          </p>
        </template>
      </template>

      <input type="hidden" name="_method" value="PATCH">
      <input type="hidden" name="_token" :value="$csrf_token">
    </form>

    <template #footer>
      <button type="button" class="btn btn-white" @click="hideModal">
        <i class="fa fa-fw fa-close"></i> Close
      </button>
      <a v-if="showAddInvoiceButton" :href="invoiceCreateRoute" class="btn btn-success">
        <i class="fa fa-fw fa-plus"></i> Add Invoice
      </a>
      <button type="submit" class="btn btn-primary" :disabled="!submitEnabled" name="button_send_for_packaging" :form="formId">
        <i class="fa fa-fw fa-save"></i> Send for packaging
      </button>
    </template>

  </modal>
</template>

<script>
import HasModal from "./../mixins/HasModal";
export default {

  /**
   *
   * Modal dialog allows to send claim for packaging with preconditions check.
   *
   * Input properties:
   *
   * modalId                - custom id for modal component.
   * action                 - form action URL.
   * title                  - form title.
   * claimTeam              - assigned claim team.
   * claimAdjuster          - assigned claim adjuster.
   * invoicesCount          - count of claim invoices.
   * examinerAssigned       - is examiner assigned (boolean value).
   * assignClaimsPermission - does the user have 'assign-claims' permission (boolean value).
   * billClaimsPermission   - does the user have 'bill-claims' permission (boolean value).
   * examiners              - list of available examiners.
   * invoiceCreateRoute     - URL of create invoices page.
   *
   * Usage examples:
   *
   * <send-for-packaging-modal action="{{ route('claims.submit', compact('claim')) }}"
   *                           modal-id="sendForPackagingModal"
   *                           title="Send for Packaging"
   *                           :examiners="{{ json_encode($examiners) }}"
   *                           :claim-team="{{ json_encode($claim->team) }}"
   *                           :claim-adjuster="{{ json_encode($claim->adjuster) }}"
   *                           :invoices-count="{{ $claim->invoices->count() }}"
   *                           :examiner-assigned="{{ strbool($claim->isExaminerAssigned()) }}"
   *                           :assign-claims-permission="{{ strbool(Auth::user()->can('assign-claims')) }}"
   *                           :bill-claims-permission="{{ strbool(Auth::user()->can('bill-claims')) }}"
   *                           invoice-create-route="{{ route('invoices.create', compact('claim')) }}">
   *   <a class="btn btn-sm btn-info" href="#" v-modal="'sendForPackagingModal'">
   *     <i class="fa fa-fw fa-share-square-o"></i> Send for Packaging
   *   </a>
   * </send-for-packaging-modal>
   *
   */

  mixins: [HasModal],

  props: {

    // Modal dialog id.
    modalId: {
      required: false,
      type: String,
      default: 'sendForPackagingModal'
    },

    // Form action URL.
    action: {
      type: String,
      default: ""
    },

    // Form title.
    title: {
      type: String,
      default: ""
    },

    claimTeam: {
      type: Object,
      default: () => {
        return {};
      }
    },

    claimAdjuster: {
      type: Object,
      default: () => {
        return {};
      }
    },

    invoicesCount: {
      type: Number,
      default: 0
    },

    examinerAssigned: {
      type: Boolean,
      default: false
    },

    assignClaimsPermission: {
      type: Boolean,
      default: false
    },

    billClaimsPermission: {
      type: Boolean,
      default: false
    },

    examiners: {
      type: [Array, Object],
      default: () => {
        return [];
      }
    },

    invoiceCreateRoute: {
      type: String,
      default: ''
    },

  },

  /**
   * Variables (models).
   */
  data() {
    return {
      data: {
        examiner: 0
      },
    }
  },

  /**
   * Computed values
   */
  computed: {

    formId() {
      return this.modalId + "-form"
    },

    showAddInvoiceButton() {
      return this.billClaimsPermission && this.invoicesCount === 0;
    },

    submitEnabled() {
      return this.examinerAssigned || this.data.examiner;
    },

  },
}
</script>
